import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Checkbox,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  Paper,
  Grid,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Switch
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { formatDate, formatDateForInput, getDurationInWeeks } from '../utils/dateUtils';
import DurationSelector from './DurationSelector';
import axios from 'axios';
import config from '../config';
import { useAuth0 } from '@auth0/auth0-react';

const ComprehensiveUpdateDialog = ({
  open,
  onClose,
  order,
  onSubmit
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [selectedUpdateTypes, setSelectedUpdateTypes] = useState({
    dates: false,
    link: false,
    artwork: false
  });

  // Date update state
  const [selectedGroupForDateUpdate, setSelectedGroupForDateUpdate] = useState(null);
  const [newEndDate, setNewEndDate] = useState('');
  const [newDuration, setNewDuration] = useState('');
  const [dateUpdateCost, setDateUpdateCost] = useState(0);
  const [dateUpdateNoCost, setDateUpdateNoCost] = useState(false);

  // Link update state
  const [selectedGroupForLinkUpdate, setSelectedGroupForLinkUpdate] = useState(null);
  const [linkUpdateType, setLinkUpdateType] = useState('');
  const [linkUpdateContent, setLinkUpdateContent] = useState('');

  // Artwork update state
  const [artworkAttachments, setArtworkAttachments] = useState([]);
  const [artworkChangeCount, setArtworkChangeCount] = useState(1);
  // null = normal cost, 'artwork_supplied' = client provided artwork ($0), 'free_artwork' = complimentary design ($0)
  const [artworkCostType, setArtworkCostType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Derived: any no-cost reason means $0
  const artworkUpdateNoCost = artworkCostType !== null;

  const [updateNotes, setUpdateNotes] = useState('');
  const [organizationNames, setOrganizationNames] = useState({});

  // Fetch organization names when dialog opens
  useEffect(() => {
    const fetchOrganizationNames = async () => {
      if (!open || !order?.audiences || order.audiences.length === 0) return;

      try {
        const token = await getAccessTokenSilently();
        const orgIds = [...new Set(order.audiences.map(a => a.organization).filter(Boolean))];
        const orgNamesMap = {};

        for (const orgId of orgIds) {
          try {
            const response = await axios.get(
              `${config.apiUrl}/api/organizations/${orgId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data) {
              orgNamesMap[orgId] = response.data.name;
            }
          } catch (err) {
            console.error(`Error fetching organization ${orgId}:`, err);
            orgNamesMap[orgId] = 'Unknown School';
          }
        }

        setOrganizationNames(orgNamesMap);
      } catch (error) {
        console.error('Error fetching organization names:', error);
      }
    };

    fetchOrganizationNames();
  }, [open, order, getAccessTokenSilently]);

  // Calculate cost when date changes
  useEffect(() => {
    if (selectedGroupForDateUpdate && newEndDate) {
      const oldDate = new Date(selectedGroupForDateUpdate.endDate);
      const newDate = new Date(newEndDate);
      const additionalDays = Math.ceil((newDate - oldDate) / (1000 * 60 * 60 * 24));
      const additionalWeeks = Math.ceil(additionalDays / 7);
      
      // Example: $10 per week extension
      const cost = Math.max(0, additionalWeeks * 10);
      setDateUpdateCost(cost);
    }
  }, [selectedGroupForDateUpdate, newEndDate]);


  const handleToggleUpdateType = (type) => {
    setSelectedUpdateTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleGroupSelection = (group, type) => {
    if (type === 'dates') {
      setSelectedGroupForDateUpdate(group);
      // Set initial new end date to current end date
      setNewEndDate(formatDateForInput(group.endDate));
      const duration = getDurationInWeeks(group.startDate, group.endDate);
      setNewDuration(duration.toString());
    } else if (type === 'link') {
      setSelectedGroupForLinkUpdate(group);
      // Pre-fill with existing link info
      setLinkUpdateType(group.eLinkType || '');
      setLinkUpdateContent(group.eLinkContent || '');
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newAttachments = files.map(file => ({
      file,
      name: file.name,
      preview: URL.createObjectURL(file)
    }));
    setArtworkAttachments(prev => [...prev, ...newAttachments]);
  };

  const handleRemoveAttachment = (index) => {
    setArtworkAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const updateData = {
      organizationId: order.organization_id || order.organization,
      updateType: 'Order Update',
      relatedOrderId: order.id,
      relatedOrderBusinessName: order.businessName,
      status: 'New',
      notes: updateNotes,
      updates: []
    };

    if (selectedUpdateTypes.dates && selectedGroupForDateUpdate) {
      const groupIndex = order?.audiences?.findIndex(a => a === selectedGroupForDateUpdate) + 1;
      const schoolName = organizationNames[selectedGroupForDateUpdate.organization] || 'Unknown';
      const groupLabel = `${groupIndex} (${selectedGroupForDateUpdate.channel} - ${selectedGroupForDateUpdate.adType} - ${schoolName})`;
      
      updateData.updates.push({
        type: 'dates',
        groupId: selectedGroupForDateUpdate.organization, // Organisation ID for backend processing
        groupLabel: groupLabel, // Human-readable label for display
        oldEndDate: selectedGroupForDateUpdate.endDate,
        newEndDate: newEndDate,
        duration: newDuration,
        cost: dateUpdateNoCost ? 0 : dateUpdateCost
      });
    }

    if (selectedUpdateTypes.link && selectedGroupForLinkUpdate) {
      const groupIndex = order?.audiences?.findIndex(a => a === selectedGroupForLinkUpdate) + 1;
      const schoolName = organizationNames[selectedGroupForLinkUpdate.organization] || 'Unknown';
      const groupLabel = `${groupIndex} (${selectedGroupForLinkUpdate.channel} - ${selectedGroupForLinkUpdate.adType} - ${schoolName})`;
      
      updateData.updates.push({
        type: 'link',
        groupId: selectedGroupForLinkUpdate.organization, // Organisation ID for backend processing
        groupLabel: groupLabel, // Human-readable label for display
        linkType: linkUpdateType,
        linkContent: linkUpdateContent,
        cost: 0
      });
    }

    if (selectedUpdateTypes.artwork) {
      updateData.updates.push({
        type: 'artwork',
        attachments: artworkAttachments,
        changeCount: artworkChangeCount,
        cost: artworkUpdateNoCost ? 0 : (artworkChangeCount * 25),
        artworkCostType: artworkCostType || null
      });
    }

    const totalCost = updateData.updates.reduce((sum, update) => sum + (update.cost || 0), 0);
    updateData.totalPrice = totalCost;

    onSubmit(updateData, artworkAttachments);
  };

  const isValid = () => {
    if (!selectedUpdateTypes.dates && !selectedUpdateTypes.link && !selectedUpdateTypes.artwork) {
      return false;
    }
    if (selectedUpdateTypes.dates && !selectedGroupForDateUpdate) {
      return false;
    }
    if (selectedUpdateTypes.link && !selectedGroupForLinkUpdate) {
      return false;
    }
    return true;
  };

  const calculateTotalCost = () => {
    let total = 0;
    if (selectedUpdateTypes.dates && !dateUpdateNoCost) {
      total += dateUpdateCost;
    }
    if (selectedUpdateTypes.artwork && !artworkUpdateNoCost) {
      total += artworkChangeCount * 25;
    }
    return total;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Create Update for Order #{order?.id}
          </Typography>
          <Chip 
            label={order?.businessName || 'No Business Name'} 
            color="primary"
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" icon={<InfoIcon />}>
            This order is Invoiced. Select the type(s) of updates you want to make below.
          </Alert>
        </Box>

        {/* Update Type Selection */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Select Update Types
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedUpdateTypes.dates}
                  onChange={() => handleToggleUpdateType('dates')}
                  icon={<CalendarIcon />}
                  checkedIcon={<CalendarIcon />}
                />
              }
              label="Change End Date"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedUpdateTypes.link}
                  onChange={() => handleToggleUpdateType('link')}
                  icon={<LinkIcon />}
                  checkedIcon={<LinkIcon />}
                />
              }
              label="Change Link"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedUpdateTypes.artwork}
                  onChange={() => handleToggleUpdateType('artwork')}
                  icon={<ImageIcon />}
                  checkedIcon={<ImageIcon />}
                />
              }
              label="Change Artwork"
            />
          </Box>
        </Paper>

        {/* Date Update Section */}
        {selectedUpdateTypes.dates && (
          <Paper sx={{ p: 3, mb: 3, borderLeft: 4, borderColor: 'primary.main' }}>
            <Typography variant="h6" gutterBottom>
              <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Change End Date
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Group Selection */}
            {!selectedGroupForDateUpdate ? (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Select which group to update:
                </Typography>
                <List>
                  {order?.audiences?.map((audience, index) => {
                    const schoolName = organizationNames[audience.organization] || 'Loading...';
                    return (
                      <ListItem
                        key={index}
                        component="button"
                        onClick={() => handleGroupSelection(audience, 'dates')}
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                          '&:hover': { bgcolor: 'action.hover' },
                          cursor: 'pointer'
                        }}
                      >
                        <ListItemText
                          primary={`Group ${index + 1} - ${audience.channel || 'N/A'} - ${audience.adType || 'N/A'} - ${schoolName}`}
                          secondary={`Current: ${formatDate(audience.startDate)} - ${formatDate(audience.endDate)} (${getDurationInWeeks(audience.startDate, audience.endDate)} weeks)`}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            ) : (
              <Box>
                <Card sx={{ mb: 2, bgcolor: 'info.50' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Selected Group
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          Group {order?.audiences?.findIndex(a => a === selectedGroupForDateUpdate) + 1} - {selectedGroupForDateUpdate.channel} - {selectedGroupForDateUpdate.adType}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          School: {organizationNames[selectedGroupForDateUpdate.organization] || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Current End Date: {formatDate(selectedGroupForDateUpdate.endDate)}
                        </Typography>
                      </Box>
                      <Button size="small" onClick={() => setSelectedGroupForDateUpdate(null)}>
                        Change Group
                      </Button>
                    </Box>
                  </CardContent>
                </Card>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      value={formatDate(selectedGroupForDateUpdate.startDate)}
                      InputProps={{ readOnly: true }}
                      helperText="Read-only"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Current End Date"
                      value={formatDate(selectedGroupForDateUpdate.endDate)}
                      InputProps={{ readOnly: true }}
                      helperText="Current end date"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DurationSelector
                      startDate={selectedGroupForDateUpdate.startDate}
                      endDate={newEndDate}
                      onEndDateChange={setNewEndDate}
                      duration={newDuration}
                      onDurationChange={setNewDuration}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <TextField
                        label="Extension Cost"
                        value={`$${dateUpdateCost.toFixed(2)}`}
                        InputProps={{ readOnly: true }}
                        disabled={dateUpdateNoCost}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={dateUpdateNoCost}
                            onChange={(e) => setDateUpdateNoCost(e.target.checked)}
                          />
                        }
                        label="No Cost"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>
        )}

        {/* Link Update Section */}
        {selectedUpdateTypes.link && (
          <Paper sx={{ p: 3, mb: 3, borderLeft: 4, borderColor: 'secondary.main' }}>
            <Typography variant="h6" gutterBottom>
              <LinkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Change Link
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {!selectedGroupForLinkUpdate ? (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Select which group to update:
                </Typography>
                <List>
                  {order?.audiences?.map((audience, index) => {
                    const schoolName = organizationNames[audience.organization] || 'Loading...';
                    return (
                      <ListItem
                        key={index}
                        component="button"
                        onClick={() => handleGroupSelection(audience, 'link')}
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                          '&:hover': { bgcolor: 'action.hover' },
                          cursor: 'pointer'
                        }}
                      >
                        <ListItemText
                          primary={`Group ${index + 1} - ${audience.channel || 'N/A'} - ${audience.adType || 'N/A'} - ${schoolName}`}
                          secondary={`Current eLink: ${audience.eLinkType || 'None'}`}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            ) : (
              <Box>
                <Card sx={{ mb: 2, bgcolor: 'secondary.50' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Selected Group
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          Group {order?.audiences?.findIndex(a => a === selectedGroupForLinkUpdate) + 1} - {selectedGroupForLinkUpdate.channel} - {selectedGroupForLinkUpdate.adType}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          School: {organizationNames[selectedGroupForLinkUpdate.organization] || 'Unknown'}
                        </Typography>
                      </Box>
                      <Button size="small" onClick={() => setSelectedGroupForLinkUpdate(null)}>
                        Change Group
                      </Button>
                    </Box>
                  </CardContent>
                </Card>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>eLink Type</InputLabel>
                      <Select
                        value={linkUpdateType}
                        onChange={(e) => setLinkUpdateType(e.target.value)}
                        label="eLink Type"
                      >
                        <MenuItem value="Website">Website</MenuItem>
                        <MenuItem value="Email">Email</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="eLink Content"
                      value={linkUpdateContent}
                      onChange={(e) => setLinkUpdateContent(e.target.value)}
                      placeholder="Enter URL, email, or phone number"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                      Link changes have no cost
                    </Alert>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>
        )}

        {/* Artwork Update Section */}
        {selectedUpdateTypes.artwork && (
          <Paper sx={{ p: 3, mb: 3, borderLeft: 4, borderColor: 'warning.main' }}>
            <Typography variant="h6" gutterBottom>
              <ImageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Change Artwork
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current Attachments:
              </Typography>
              {order?.attachments && order.attachments.length > 0 ? (
                <List dense>
                  {order.attachments.map((attachment, index) => (
                    <ListItem key={index}>
                      <AttachFileIcon sx={{ mr: 1 }} fontSize="small" />
                      <ListItemText primary={attachment.name || `Attachment ${index + 1}`} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No current attachments
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<AddIcon />}
                fullWidth
              >
                Add New Artwork Files
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*,.pdf"
                  onChange={handleFileUpload}
                />
              </Button>
            </Box>

            {artworkAttachments.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  New Attachments:
                </Typography>
                <List dense>
                  {artworkAttachments.map((attachment, index) => (
                    <ListItem
                      key={index}
                      secondaryAction={
                        <IconButton edge="end" onClick={() => handleRemoveAttachment(index)}>
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                      <AttachFileIcon sx={{ mr: 1 }} fontSize="small" />
                      <ListItemText primary={attachment.name} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                type="number"
                label="Number of Artwork Changes"
                value={artworkChangeCount}
                onChange={(e) => setArtworkChangeCount(Math.max(0, parseInt(e.target.value) || 0))}
                inputProps={{ min: 0 }}
                helperText="Enter the number of separate artwork changes (each change is $25)"
              />
            </Box>

            <Alert 
              severity="warning" 
              sx={{ mb: 2 }}
              icon={<InfoIcon />}
            >
              {artworkChangeCount} artwork change{artworkChangeCount !== 1 ? 's' : ''} × $25 = ${(artworkChangeCount * 25).toFixed(2)}
            </Alert>

            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                $0 Reason (optional — select if artwork cost should be waived):
              </Typography>
              <ToggleButtonGroup
                value={artworkCostType}
                exclusive
                onChange={(_, newValue) => setArtworkCostType(newValue)}
                size="small"
              >
                <ToggleButton value="artwork_supplied" color="success">
                  Artwork Supplied
                </ToggleButton>
                <ToggleButton value="free_update" color="info">
                  Free Update
                </ToggleButton>
              </ToggleButtonGroup>
              {artworkCostType && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {artworkCostType === 'artwork_supplied'
                    ? 'Client provided their own artwork — no proof needed, no design charge.'
                    : 'Update provided free of charge — proof will be sent, no charge to client.'}
                </Typography>
              )}
            </Box>
          </Paper>
        )}

        {/* Notes Section */}
        <TextField
          fullWidth
          label="Additional Notes"
          value={updateNotes}
          onChange={(e) => setUpdateNotes(e.target.value)}
          multiline
          rows={3}
          placeholder="Add any additional notes about this update..."
        />

        {/* Cost Summary */}
        {(selectedUpdateTypes.dates || selectedUpdateTypes.link || selectedUpdateTypes.artwork) && (
          <Paper sx={{ p: 2, mt: 3, bgcolor: 'success.50' }}>
            <Typography variant="h6" gutterBottom>
              Update Summary
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>Selected Updates:</Typography>
              <Typography fontWeight="bold">
                {[
                  selectedUpdateTypes.dates && 'Date Extension',
                  selectedUpdateTypes.link && 'Link Change',
                  selectedUpdateTypes.artwork && 'Artwork Change'
                ].filter(Boolean).join(', ')}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            {selectedUpdateTypes.artwork && artworkCostType && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Artwork $0 reason:</Typography>
                <Chip
                  size="small"
                  label={artworkCostType === 'artwork_supplied' ? 'Artwork Supplied' : 'Free Update'}
                  color={artworkCostType === 'artwork_supplied' ? 'success' : 'info'}
                  variant="outlined"
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6">Total Cost:</Typography>
              <Typography variant="h6" color="success.main">
                ${calculateTotalCost().toFixed(2)}
              </Typography>
            </Box>
          </Paper>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid() || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isSubmitting ? 'Creating...' : 'Create Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ComprehensiveUpdateDialog;

